import { Spinner } from '@latitude/spinner';
import { useActionListContext } from 'ActionListContext';
import { CommandInput as CommandInputPrimitive } from 'cmdk';
import { Command } from 'components/Command';
import { CommandEmpty } from 'components/CommandEmpty';
import { CommandGroupContent } from 'components/CommandGroupContent';
import { CommandInput } from 'components/CommandInput';
import { CommandList } from 'components/CommandList';
import { CommandSelectAll } from 'components/CommandSelectAll';
import { noop } from 'lodash-es';
import type { ComponentRef, Ref } from 'react';
import { useEffect, useId, useState } from 'react';
import type { CommandLayoutProps } from 'types';

export const CommandLayout = ({
  disabled,
  loading,
  isMulti,
  withSearch,
  placeholder = 'Search...',
  itemSize,
  showSelectionElement,
  noResultsText,
  rawProps,
  searchTerm,
  withoutPopover,
  onSearchTermChange = noop,
  onScroll = noop,
  listRef,
  truncateOptions = false,
  selectAllLabel,
  hideScrollbar,
  maxCharacters,
  characterLimitReachedLabel,
  error,
  errorMessage,
  highlightSelectedItem,
  ref,
}: CommandLayoutProps & {
  ref?: Ref<ComponentRef<typeof CommandInputPrimitive>>;
}) => {
  // cmdk moves its keyboard highlight (data-selected) with the pointer but
  // never clears it, so the last hovered item stays highlighted after the
  // pointer leaves the list. Control the highlight so we can clear it on mouse
  // leave; keyboard navigation recovers from an empty selection.
  //
  // cmdk also auto-highlights the FIRST item whenever its value is falsy (on
  // mount and on option changes), painting a phantom hover on the first row
  // even when the pointer never entered the list. Opt-in `highlightSelectedItem`
  // seeds the controlled highlight with the current single selection so the
  // selected row keeps its highlight (and aria-selected) and lists that don't
  // contain it highlight nothing. Off by default: every other consumer keeps
  // cmdk's empty-seed behavior — a non-empty value forces cmdk to scan item
  // values (calling `.trim()`), which throws for consumers with non-string
  // item values. `selectedValues` is typed string but at runtime may be an
  // array (multi) / null (nullable) / non-string, so only adopt a real string.
  const { selectedValues } = useActionListContext();
  const singleSelection =
    typeof selectedValues === 'string' ? selectedValues : undefined;
  const restingHighlight =
    highlightSelectedItem && singleSelection ? singleSelection : '';
  const [highlightedValue, setHighlightedValue] = useState(restingHighlight);

  const errorMessageId = useId();
  const describedById = errorMessage ? errorMessageId : undefined;

  // Keep the resting highlight in sync as the selection changes (e.g. the host
  // navigates to a different item) so aria-selected tracks the current item;
  // hover moves the highlight transiently and mouse-leave restores this.
  useEffect(() => {
    setHighlightedValue(restingHighlight);
  }, [restingHighlight]);

  return (
    <Command
      {...rawProps}
      aria-disabled={disabled}
      shouldFilter={false}
      value={highlightedValue}
      onValueChange={setHighlightedValue}
      //https://github.com/radix-ui/primitives/issues/1159#issuecomment-2141403778
      onWheel={(e) => e.stopPropagation()}
    >
      {withSearch && (
        <CommandInput
          autoFocus
          placeholder={placeholder}
          ref={ref}
          value={searchTerm}
          inputSize={itemSize}
          maxCharacters={maxCharacters}
          characterLimitReachedLabel={characterLimitReachedLabel}
          error={error}
          errorMessageId={describedById}
          onValueChange={onSearchTermChange}
          onClear={() => onSearchTermChange('')}
        />
      )}
      {errorMessage && (
        <div
          id={errorMessageId}
          className="pr-spacing-comp-action-list-medium-horizontal pb-spacing-comp-action-list-medium-vertical"
        >
          {errorMessage}
        </div>
      )}
      {!loading && (
        <CommandSelectAll itemSize={itemSize} label={selectAllLabel} />
      )}
      <CommandList
        aria-multiselectable={isMulti ?? false}
        hasSearchInput={withSearch}
        withoutPopover={withoutPopover}
        hideScrollbar={hideScrollbar}
        onScroll={(e) => onScroll(e, searchTerm)}
        onMouseLeave={() => setHighlightedValue(restingHighlight)}
        ref={listRef}
      >
        {loading ? (
          <CommandEmpty>
            <Spinner />
          </CommandEmpty>
        ) : (
          <CommandGroupContent
            itemSize={itemSize}
            showSelectionElement={showSelectionElement ?? false}
            noResultsText={noResultsText}
            truncateOptions={truncateOptions}
          />
        )}
      </CommandList>
    </Command>
  );
};

CommandLayout.displayName = CommandInputPrimitive.displayName;
